import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CONCEPTS, getConceptById } from '@/data/concepts.ts';
import { MaterialIcon } from '@/components/ui/MaterialIcon';

function formatTitle(title: string) {
  const words = title.trim().split(/\s+/).filter(Boolean);

  if (words.length <= 1) {
    return { lead: words[0] ?? title, tail: '' };
  }

  return {
    lead: words[0],
    tail: words.slice(1).join(' '),
  };
}

function relatedProgress(index: number) {
  const progress = ['w-2/3', 'w-1/4', 'w-1/2', 'w-3/5'];
  return progress[index % progress.length];
}

export default function ConceptPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const concept = id ? getConceptById(id) : null;
  const backTarget = (location.state as { from?: string } | null)?.from ?? '/concepts';
  const returnLabel = backTarget.startsWith('/courses/') ? '⬅ Return to Quest' : '⬅ Return';

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(backTarget);
  };

  if (!concept) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-6 py-24 text-center">
        <div className="mb-6 flex h-20 w-20 items-center justify-center bg-[#131313] text-5xl text-[#8cacff]">
          <MaterialIcon icon="report" size={34} className="text-[#8cacff]" />
        </div>
        <span className="mb-4 font-['Space_Grotesk'] text-xs font-[900] uppercase tracking-[0.35em] text-[#00cffc]">
          SYSTEM_FAULT
        </span>
        <h2 className="font-['Space_Grotesk'] text-3xl font-[900] uppercase tracking-tight text-white md:text-5xl">
          Concept not found in the vault
        </h2>
        <p className="mt-4 max-w-xl font-['Inter'] text-sm leading-relaxed text-[#acabaa] md:text-base">
          The briefing you requested is missing from the knowledge graph. Return to the vault or backtrace your previous path.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link
            to="/concepts"
            className="bg-gradient-to-r from-[#8cacff] to-[#769dff] px-6 py-3 font-['Space_Grotesk'] text-xs font-[900] uppercase tracking-[0.18em] text-[#002a6d] transition-all hover:brightness-110"
          >
            RETURN TO VAULT
          </Link>
          <button
            type="button"
            onClick={goBack}
            className="bg-[#1f2020] px-6 py-3 font-['Space_Grotesk'] text-xs font-[900] uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#252626]"
          >
            {returnLabel}
          </button>
        </div>
      </div>
    );
  }

  const related = concept.related?.map((rid: string) => CONCEPTS[rid]).filter(Boolean) ?? [];
  const title = formatTitle(concept.title);
  const slug = id?.toUpperCase() ?? concept.id.toUpperCase();
  const briefingSnippet = [
    'const conceptBriefing = {',
    `  id: ${JSON.stringify(concept.id)},`,
    `  title: ${JSON.stringify(concept.title)},`,
    `  summary: ${JSON.stringify(concept.summary)},`,
    '};',
  ].join('\n');

  return (
    <div className="bg-[#0e0e0e] text-white">
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-20"
        style={{
          backgroundImage:
            'linear-gradient(to bottom, transparent 50%, rgba(0, 207, 252, 0.05) 50%)',
          backgroundSize: '100% 4px',
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1440px] px-6 pb-24 pt-8 md:pb-32 md:pt-10">
        <div className="mb-4 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#00cffc]">
          <span>INTEL_VAULT</span>
          <span className="text-[#767575]">/</span>
          <span>CORE_CONCEPTS</span>
          <span className="text-[#767575]">/</span>
          <span className="text-white">{slug}</span>
        </div>

        <div className="grid grid-cols-12 gap-8">
          <section className="col-span-12 lg:col-span-8">
            <header className="mb-12">
              <h1 className="font-['Space_Grotesk'] text-6xl font-[900] uppercase leading-none tracking-tighter md:text-8xl">
                <span className="block text-white">{title.lead}</span>
                {title.tail ? (
                  <span className="block text-transparent bg-gradient-to-r from-[#8cacff] to-[#00cffc] bg-clip-text">
                    {title.tail}
                  </span>
                ) : null}
              </h1>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <span className="bg-[#8cacff]/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8cacff]">
                  RANK: ELITE
                </span>
                <span className="bg-[#252626] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#767575]">
                  ID: {slug}
                </span>
                <span className="bg-[#d674ff]/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#d674ff]">
                  TAGS: {concept.tags.length || 0}
                </span>
              </div>
            </header>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative mb-12 overflow-hidden bg-[#131313] p-8"
            >
              <div className="absolute right-4 top-4 select-none font-mono text-[10px] uppercase tracking-[0.25em] text-[#767575]/30">
                TRUTH_ORDINAL: 001
              </div>
              <div className="flex gap-6">
                <div className="h-24 w-1 bg-gradient-to-b from-[#8cacff] to-transparent" />
                <div className="min-w-0">
                  <h2 className="mb-4 font-['Space_Grotesk'] text-xl font-[900] uppercase tracking-tight text-white">
                    CONCEPT_SUMMARY
                  </h2>
                  <p className="max-w-2xl text-lg leading-relaxed text-[#acabaa]">
                    {concept.summary}
                  </p>
                </div>
              </div>
            </motion.section>

            <section className="mb-16">
              <div className="mb-8 flex items-center gap-4">
                <h3 className="font-['Space_Grotesk'] text-2xl font-[900] uppercase tracking-[0.2em] text-white">
                  TACTICAL_INTELLIGENCE
                </h3>
                <div className="h-px flex-1 bg-[#484848]/15" />
              </div>

              <div className="space-y-10">
                <div className="grid gap-8 md:grid-cols-2">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-[#00cffc]">
                      <MaterialIcon icon="settings_input_component" size={18} className="text-[#00cffc]" />
                      <span className="font-mono text-[11px] uppercase tracking-[0.25em]">
                        IMPLEMENTATION_LOGIC
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed text-[#acabaa]">
                      {concept.summary}
                    </p>
                    <p className="text-sm leading-relaxed text-[#acabaa]">
                      Use the source-of-truth concept page to keep related lessons aligned and prevent duplicated explanations across the curriculum.
                    </p>
                  </div>

                  <div className="overflow-x-auto bg-[#252626] p-4 font-mono text-xs">
                    <pre className="whitespace-pre-wrap text-[#eef9ff]">
{briefingSnippet}
                    </pre>
                  </div>
                </div>

                <div className="border-l-4 border-[#8cacff] bg-gradient-to-r from-[#8cacff]/10 to-transparent p-6">
                  <div className="mb-2 flex items-center gap-3">
                    <MaterialIcon icon="bolt" size={18} fill className="text-[#8cacff]" />
                    <h4 className="font-['Space_Grotesk'] text-sm font-[900] uppercase tracking-[0.25em] text-[#8cacff]">
                      PRO_TIP: RE-ENTRY_PREVENTION
                    </h4>
                  </div>
                  <p className="text-sm text-white">
                    Keep the concept summary and related nodes in sync before shipping course content. One concept should own the explanation; everything else should reference it.
                  </p>
                </div>
              </div>
            </section>
          </section>

          <aside className="col-span-12 space-y-6 lg:col-span-4">
            <div className="bg-[#1f2020] p-6">
              <div className="mb-6 flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-['Space_Grotesk'] text-sm font-[900] uppercase tracking-[0.25em] text-[#8cacff]">
                  <MaterialIcon icon="hub" size={18} className="text-[#8cacff]" />
                  RELATED_INTEL
                </h3>
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#767575]">
                  [SCAN_ACTIVE]
                </span>
              </div>

              <div className="space-y-4">
                {related.slice(0, 2).map((rel: any, index: number) => (
                  <Link
                    key={rel.id}
                    to={`/concepts/${rel.id}`}
                    state={{ from: location.pathname }}
                    className="group block bg-[#131313] p-4 transition-colors hover:bg-[#252626]"
                  >
                    <div className="mb-2 flex justify-between">
                      <span
                        className={`font-mono text-[10px] uppercase tracking-[0.25em] ${index === 0 ? 'text-[#00cffc]' : 'text-[#d674ff]'}`}
                      >
                        INTEL_{String(index + 42).padStart(3, '0')}
                      </span>
                      <MaterialIcon
                        icon="arrow_forward_ios"
                        size={12}
                        className="text-[#767575] transition-colors group-hover:text-[#8cacff]"
                      />
                    </div>
                    <h4 className="font-['Space_Grotesk'] text-lg font-[900] uppercase tracking-tight text-white transition-colors group-hover:text-[#8cacff]">
                      {rel.title}
                    </h4>
                    <div className="mt-2 h-1 w-full bg-[#252626]">
                      <div className={`h-full ${index === 0 ? 'bg-[#00cffc]' : 'bg-[#d674ff]'} ${relatedProgress(index)}`} />
                    </div>
                  </Link>
                ))}

                {related.length === 0 ? (
                  <div className="bg-[#131313] p-4 text-sm text-[#acabaa]">
                    No related intel indexed for this concept yet.
                  </div>
                ) : null}
              </div>
            </div>

            <div className="bg-[#131313] p-6 font-mono text-[11px] uppercase tracking-[0.25em]">
              <div className="mb-4 text-[#767575]">SYSTEM_READOUT</div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>MASTERY_LEVEL</span>
                  <span className="text-[#8cacff]">
                    {Math.min(98, 68 + concept.tags.length * 3)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>COMPLETION_TIME</span>
                  <span className="text-[#00cffc]">
                    4:{String(12 + related.length).padStart(2, '0')}M
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>REWARD_POOL</span>
                  <span className="text-[#d674ff]">
                    {`${Math.max(1, concept.tags.length + related.length) * 300}XP`}
                  </span>
                </div>
              </div>
            </div>

            <div className="relative aspect-video overflow-hidden bg-[#131313]">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    'radial-gradient(circle at 20% 20%, rgba(140, 172, 255, 0.35), transparent 25%), radial-gradient(circle at 80% 30%, rgba(0, 207, 252, 0.22), transparent 28%), radial-gradient(circle at 50% 80%, rgba(214, 116, 255, 0.18), transparent 30%), linear-gradient(135deg, rgba(31, 32, 32, 0.7), rgba(14, 14, 14, 0.95))',
                }}
              />
              <div className="absolute inset-0 opacity-35 [background-image:linear-gradient(rgba(76,94,122,0.35)_1px,transparent_1px),linear-gradient(90deg,rgba(76,94,122,0.22)_1px,transparent_1px)] [background-size:32px_32px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e0e] via-transparent to-transparent" />
              <div className="absolute left-4 bottom-4 font-mono text-[9px] uppercase tracking-[0.25em] text-[#767575]">
                VISUALIZING_CORE_LOGIC.png
              </div>
            </div>
          </aside>
        </div>

        <div className="sticky bottom-4 z-20 mt-10 bg-[#0e0e0e]/90 px-4 py-4 shadow-[0_0_40px_rgba(0,0,0,0.35)] backdrop-blur-xl md:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-8">
              <div className="hidden sm:flex flex-col">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#767575]">
                  CURRENT_MODULE
                </span>
                <span className="font-['Space_Grotesk'] text-sm font-[900] uppercase text-white">
                  {concept.title.replace(/\s+/g, '_').toUpperCase()}
                </span>
              </div>

              <div className="hidden h-8 w-px bg-[#484848]/30 sm:block" />

              <div className="flex items-center gap-4">
                {related[0] ? (
                  <Link
                    to={`/concepts/${related[0].id}`}
                    state={{ from: location.pathname }}
                    className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#484848] transition-colors hover:text-[#00cffc]"
                  >
                    <MaterialIcon icon="skip_previous" size={16} className="text-inherit" />
                    PREV
                  </Link>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#484848] opacity-50"
                  >
                    <MaterialIcon icon="skip_previous" size={16} className="text-inherit" />
                    PREV
                  </button>
                )}

                {related[1] ? (
                  <Link
                    to={`/concepts/${related[1].id}`}
                    state={{ from: location.pathname }}
                    className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#484848] transition-colors hover:text-[#00cffc]"
                  >
                    NEXT
                    <MaterialIcon icon="skip_next" size={16} className="text-inherit" />
                  </Link>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#484848] opacity-50"
                  >
                    NEXT
                    <MaterialIcon icon="skip_next" size={16} className="text-inherit" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/concepts"
                className="flex items-center gap-2 bg-[#1f2020] px-6 py-2 font-['Space_Grotesk'] text-xs font-[900] uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#252626]"
              >
                <MaterialIcon icon="download" size={14} className="text-white" />
                ARCHIVE
              </Link>
              <Link
                to="/courses"
                className="flex items-center gap-2 bg-gradient-to-r from-[#00cffc] to-[#00586d] px-6 py-2 font-['Space_Grotesk'] text-xs font-[900] uppercase tracking-[0.25em] text-white shadow-[0_0_15px_rgba(0,207,252,0.3)] transition-all hover:shadow-[0_0_25px_rgba(0,207,252,0.5)]"
              >
                <MaterialIcon icon="swords" size={14} className="text-white" />
                ENTER_ARENA
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
